import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  afterNextRender,
  computed,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Button } from 'primeng/button';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Dialog } from 'primeng/dialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Textarea } from 'primeng/textarea';
import { Tooltip } from 'primeng/tooltip';
import { ClipboardService } from '@brighthub/utils';
import {
  ACTION_TYPE_META,
  Activity,
  Citation,
  DISPOSITION_META,
  STATUS_META,
  ReviewAction,
  dispositionBreakdown,
  relativeTime,
  settledReason,
  triggeringQuote,
} from '../../models/activity.model';
import {
  ActivityInboxService,
  describeHttpError,
} from '../../services/activity-inbox.service';

/** One row of the collapsible Debug Info panel. */
interface DebugRow {
  label: string;
  value: string | null | undefined;
  /** Deep link rendered as a jump-out next to the value, when one exists. */
  href?: string | null;
}

/** A past note, with its timestamp pre-formatted for display. */
interface DisplayNote {
  note: string;
  actor: string;
  absolute: string;
  relative: string;
}

/** Actions that change state irreversibly enough to warrant confirming.
 * `retry` re-invokes the router agent for real; `deny` is terminal. */
const CONFIRMED_ACTIONS: ReviewAction[] = ['retry', 'deny'];

/**
 * The review dialog for a single activity: what the router agent proposed, the
 * conversation evidence behind it, and the FA's confirm / retry / deny decision.
 *
 * All feedback goes through `MessageService` toasts, and the only second layer
 * permitted over this dialog is the "are you sure" `p-confirmDialog` — no
 * bespoke inline panels, per Harsh's 2026-08-06 review.
 */
@Component({
  selector: 'air-activity-detail-dialog',
  imports: [CommonModule, Button, ConfirmDialog, Dialog, Textarea, Tooltip],
  providers: [ConfirmationService],
  templateUrl: './activity-detail-dialog.component.html',
  styleUrl: './activity-detail-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivityDetailDialogComponent {
  private readonly _inbox = inject(ActivityInboxService);
  private readonly _messages = inject(MessageService);
  private readonly _confirmation = inject(ConfirmationService);
  private readonly _clipboard = inject(ClipboardService);
  private readonly _injector = inject(Injector);

  readonly activity = input.required<Activity>();

  /** Shown in the header in place of the raw `client_guid`. Null until the
   * client's subject data resolves, so the GUID is the fallback rather than a
   * blank header. It stays in the Debug panel either way. */
  readonly clientName = input<string | null>(null);

  readonly visible = model(false);

  /** Emitted after any write, so the list can re-read the feed. */
  readonly changed = output<void>();

  readonly noteDraft = signal('');
  readonly debugOpen = signal(false);
  /** Which action is mid-flight, so only its button shows a spinner. */
  readonly busy = signal<ReviewAction | null>(null);
  /** Indices of the action cards whose rationale is expanded. Collapsed by
   * default: rationale is the tallest block on a card, and the modal reads as
   * too dense with every one of them open. */
  readonly expandedRationales = signal<ReadonlySet<number>>(new Set());

  readonly statusMeta = STATUS_META;
  readonly actionTypeMeta = ACTION_TYPE_META;
  readonly dispositionMeta = DISPOSITION_META;

  readonly proposal = computed(() => this.activity().router_proposal);
  readonly actions = computed(() => this.proposal().actions);
  readonly citations = computed(() => this.proposal().citations);

  readonly proposedAgo = computed(() =>
    relativeTime(this.activity().created_at),
  );
  readonly breakdown = computed(() => dispositionBreakdown(this.activity()));
  readonly quote = computed(() => triggeringQuote(this.activity()));

  /** The worst of the action dispositions, derived server-side. Shown as a chip
   * so the roll-up is visible rather than implied — and so it is legible why
   * this can differ from the activity's status while no executor exists. */
  readonly rollup = computed(() => this.proposal().disposition);

  /** Closed activities take no further action — the API answers 409 — so the
   * footer explains why instead of offering buttons that cannot succeed.
   * Null while the activity is still open. */
  readonly settled = computed(() => settledReason(this.activity()));

  /** Oldest first, so the panel reads as a history rather than a stack. */
  readonly notes = computed<DisplayNote[]>(() =>
    this.activity().notes.map((note) => ({
      note: note.note,
      actor: note.actor,
      absolute: this._formatStamp(note.created_at),
      relative: relativeTime(note.created_at),
    })),
  );

  /** "Client chat · Aug 5, 10:11 AM" above the triggering quote. */
  readonly triggerSource = computed(() => {
    const event = this.activity().enriched_event;
    const channel =
      event.channel === 'intercom' ? 'Client chat' : event.channel;
    const stamp = this._formatStamp(event.window_end);
    return stamp ? `${channel} \u00b7 ${stamp}` : channel;
  });

  /** Retry feeds the note back to the router agent as the correction to act on,
   * so it can't be blank. */
  readonly retryBlocked = computed(() => this.noteDraft().trim().length === 0);

  readonly debugRows = computed<DebugRow[]>(() => {
    const activity = this.activity();
    return [
      { label: 'Activity ID', value: activity.activity_id },
      { label: 'Client GUID', value: activity.client_guid },
      {
        label: 'Intercom conversation ID',
        value: activity.intercom_conversation_id,
        href: activity.conversation_link,
      },
      { label: 'Intercom contact ID', value: activity.intercom_contact_id },
      { label: 'Router agent', value: activity.router_agent_label },
      { label: 'Router agent ID', value: activity.router_agent_id },
      {
        label: 'Router agent version ID',
        value: activity.router_agent_version_id,
      },
      { label: 'Router run ID', value: activity.router_run_id },
      {
        label: 'Agent conversation ID',
        value: activity.router_agent_conversation_id,
      },
      { label: 'Schema version', value: activity.schema_version },
    ];
  });

  /** Cited message parts live down here rather than under "What triggered
   * this": the first citation's excerpt is already the blockquote up there, so
   * listing them again showed the same sentence twice. */
  readonly citedParts = computed<Citation[]>(() => this.citations());

  private readonly _debugSection =
    viewChild<ElementRef<HTMLElement>>('debugSection');

  close(): void {
    this.visible.set(false);
  }

  /** Debug sits at the bottom of a scrolling body, so opening it off-screen
   * looks like nothing happened. Pull it into view once its rows exist. */
  toggleDebug(): void {
    const opening = !this.debugOpen();
    this.debugOpen.set(opening);
    if (!opening) return;

    afterNextRender(
      () =>
        this._debugSection()?.nativeElement.scrollIntoView({
          block: 'end',
          behavior: 'smooth',
        }),
      { injector: this._injector },
    );
  }

  toggleRationale(index: number): void {
    const next = new Set(this.expandedRationales());
    if (!next.delete(index)) next.add(index);
    this.expandedRationales.set(next);
  }

  isRationaleOpen(index: number): boolean {
    return this.expandedRationales().has(index);
  }

  copy(row: DebugRow): void {
    if (row.value) this._clipboard.copy(row.value, `${row.label} copied`);
  }

  requestAction(action: ReviewAction): void {
    if (action === 'retry' && this.retryBlocked()) {
      this._messages.add({
        severity: 'warn',
        summary: 'Add a note first',
        detail: 'Retry sends your note to the router agent as feedback.',
        life: 4000,
      });
      return;
    }

    if (!CONFIRMED_ACTIONS.includes(action)) {
      void this._apply(action);
      return;
    }

    this._confirmation.confirm({
      header: action === 'retry' ? 'Retry this activity' : 'Deny this activity',
      message:
        action === 'retry'
          ? 'This re-invokes the router agent with your note as feedback.'
          : "Denying is a terminal status change and can't be undone.",
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: action === 'retry' ? 'Yes, retry' : 'Yes, deny',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass:
        action === 'retry' ? 'p-button-primary' : 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => void this._apply(action),
    });
  }

  private async _apply(action: ReviewAction): Promise<void> {
    this.busy.set(action);
    const note = this.noteDraft().trim() || undefined;

    try {
      await this._inbox.applyAction(this.activity().activity_id, action, note);
      this.noteDraft.set('');
      this._messages.add({
        severity: 'success',
        summary:
          action === 'retry'
            ? 'Sent back to the router agent'
            : `Activity ${action === 'deny' ? 'denied' : 'confirmed'}`,
        life: 2500,
      });
      this.changed.emit();
      // Retry stays open so the FA can read the revised proposal in place.
      if (action !== 'retry') this.visible.set(false);
    } catch (err) {
      const message = describeHttpError(err);
      // Confirm is wired end to end but has nothing to hand off to yet, so a
      // 501 is the route working as designed rather than a failure.
      if (action === 'confirm' && message.includes('501')) {
        this._messages.add({
          severity: 'info',
          summary: 'Not executable yet',
          detail:
            'Confirm reached the API, but no execution agent is wired up yet (501).',
          life: 5000,
        });
      } else {
        this._messages.add({
          severity: 'error',
          summary: `Could not ${action} this activity`,
          detail: message,
          life: 8000,
        });
      }
    } finally {
      this.busy.set(null);
    }
  }

  /** "Aug 5, 10:11 AM", or an empty string for an unparseable timestamp. */
  private _formatStamp(iso: string | null | undefined): string {
    if (!iso) return '';
    const when = Date.parse(iso);
    if (Number.isNaN(when)) return '';
    return new Date(when).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }
}
